// File: src/pages/AddWebsitePage.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  BlockStack,
  Frame, // Import Frame
} from '@shopify/polaris';

import { AuthContext } from '../context/AuthContext';
import SiteCard from '../components/SiteCard';
import TopNav from '../components/Layout/TopNav'; // Import TopNave

export default function AddWebsitePage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
  const location = useLocation();

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  // Initialize error and success from location state for initial render
  const [error, setError] = useState(location.state?.error || null);
  const [success, setSuccess] = useState(location.state?.success || null);

  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [platform, setPlatform] = useState('custom');

  const platformOptions = [
    { label: 'Custom Website', value: 'custom' },
    { label: 'Shopify', value: 'shopify' },
    { label: 'Stan.Store', value: 'stanstore' },
    { label: 'WooCommerce', value: 'woocommerce' },
    { label: 'BigCommerce', value: 'bigcommerce' },
  ];

  const fetchSites = useCallback(async () => {
    setLoading(true);
    // Do not clear error/success from location state immediately here
    // as they need to be displayed first.
    try {
      const res = await fetch(`${API}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to fetch sites: ${res.status} ${errorText}`);
      }
      const data = await res.json();
      console.log('[AddWebsitePage] Sites from API:', data);
      
      // Stop re-computing it on the client - use data directly from backend
      const sites = data; //
      setSites(sites); //

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  // Initial fetch of sites when the component mounts or token/API changes
  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  // NEW: after a BigCommerce connect, we got a `success` state → reload your sites
  useEffect(() => {
    if (success) {
      console.log("Success state detected, re-fetching sites...");
      fetchSites();
      // Optionally clear local success state after fetching to prevent re-triggering
      // setSuccess(null); // Decided to keep for Banner display until next navigation
    }
  }, [success, fetchSites]); // Depend on success and fetchSites

  // Effect to clear location state after it's been consumed for banners
  useEffect(() => {
    if (location.state?.success || location.state?.error) {
      // Clear the location state so it doesn’t persist on subsequent visits or refreshes
      // This must happen after the local state (error, success) has been set
      console.log("Clearing location state:", location.state);
      navigate(location.pathname, { replace: true, state: {} }); // Pass an empty state
    }
  }, [location, navigate]); // Depend on location and navigate

  const handleAdd = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null); // Clear success message on new add attempt

      // ADD THIS SITE LIMIT CHECK
    // ADD THIS SITE LIMIT CHECK
    const MAX_SITES = 1; // Or get from user's plan
    if (sites.length >= MAX_SITES) { // <-- Dependency: sites.length is used here
      setError(`You've reached the maximum of ${MAX_SITES} site per account. Please contact support to upgrade.`);
      setLoading(false);
      return;
    }

    if (platform === 'bigcommerce') {
      try {
        const res = await fetch(`${API}/api/sites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Couldn’t load your existing sites.');
        const allSites = await res.json();

        const bcSite = allSites.find(s => s.platform && s.platform.toLowerCase() === 'bigcommerce');
        if (bcSite) {
          setError('A BigCommerce site might already be installed. Please launch your app from the BigCommerce Marketplace, or refresh this page if you just connected it.');
          setLoading(false);
          return;
        }

        // Point to the backend's OAuth "start" URL
        const bigCommerceInstallUrl = `${API.replace(/\/api$/, '')}/bigcommerce/auth/start`;
        
        window.location.href = bigCommerceInstallUrl;
        setLoading(false);
        return;

      } catch (err) {
        setError(err.message);
        setLoading(false);
        return;
      }
    }

    try {
      const normalizedUrl =
        platform === 'custom' && !/^https?:\/\//i.test(newUrl) && newUrl.trim() !== ''
          ? `https://${newUrl}`
          : newUrl;

      const payload = { name: newName, url: normalizedUrl, platform };
      const res = await fetch(`${API}/api/sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      // ADDED: Check for 409 Conflict status
      if (res.status === 409) {
        const body = await res.json();
        setError(body.detail || 'Only one site per account is allowed.');
        setLoading(false);
        return;
      }

      if (!res.ok) {
        const errJson = await res.json();
        throw new Error(errJson.detail?.[0]?.msg || res.statusText);
      }

      const created = await res.json();
      setSites(prevSites => [...prevSites, created]);
      setNewName('');
      setNewUrl('');

      if (platform === 'stanstore') {
        window.alert(
          'Stan.Store doesn’t allow embedding custom tracking scripts. You’ll be redirected to setup instructions.'
        );
        navigate(`/setup-stan-store?siteId=${created.id}&siteToken=${encodeURIComponent(created.token)}`, { state: { site: created } });
      } else if (platform === 'shopify') {
        navigate(`/connect-shopify?siteId=${created.id}&siteToken=${encodeURIComponent(created.token)}`, { state: { site: created } });
      } else if (platform === 'woocommerce') {
        navigate(`/connect-woocommerce?siteId=${created.id}&siteToken=${encodeURIComponent(created.token)}`, { state: { site: created } });
      } else {
        navigate(`/install-custom-script?siteId=${created.id}&siteToken=${encodeURIComponent(created.token)}`, { state: { site: created } });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API, platform, token, newName, newUrl, navigate, sites.length]);

  const handleDeleteSite = useCallback((deletedSiteId) => {
    // Remove from local state
    setSites(prevSites => prevSites.filter(site => site.id !== deletedSiteId));
    setSuccess('Site disconnected successfully!');
    setTimeout(() => setSuccess(null), 3000);
  }, []);

  if (loading) {
    return (
      <SkeletonPage title="Connected Sites">
        <SkeletonBodyText lines={4} />
      </SkeletonPage>
    );
  }

  const pageTitle = sites.length === 0 ? 'Connect Your First Site' : 'Connected Sites';

  const addButtonText = platform === 'bigcommerce' ? 'Initiate BigCommerce Install' : 'Add Site';

  return (
    <Frame> {/* Wrap with Frame */}
      <TopNav /> {/* Added TopNav here, outside the Page component */}
      <Page
        title={pageTitle}
        backAction={{
          content: 'Back to Dashboard',
          onAction: () => navigate('/Dashboard'),
        }}
      >
        {success && <Banner status="success">{success}</Banner>}
        {error && <Banner status="critical">{error}</Banner>}

        <Layout>
          <Layout.Section>
            <Card sectioned>
              <BlockStack spacing="tight">
                <Select
                  label="Select Your Website Platform"
                  options={platformOptions}
                  value={platform}
                  onChange={setPlatform}
                  helpText="Choose the e-commerce platform or website builder you are currently using."
                />
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card title="Add a New Site" sectioned>
              <FormLayout>
                <TextField
                  label="Site Name"
                  value={newName}
                  onChange={setNewName}
                  placeholder="e.g. My Online Store"
                />
                <TextField
                  label="Website URL (Required for Custom Websites)"
                  value={newUrl}
                  onChange={setNewUrl}
                  helpText={
                    platform === 'shopify'
                      ? 'Leave blank to link a Shopify store (URL will be auto-detected).'
                      : (platform === 'bigcommerce'
                          ? 'This field is not needed for BigCommerce, as the store is linked via OAuth.'
                          : 'Enter the full URL of your website (e.g., https://www.yourwebsite.com).')
                  }
                  placeholder={
                    platform === 'shopify' || platform === 'bigcommerce' ? '' : 'https://www.yourwebsite.com'
                  }
                  disabled={platform === 'shopify' || platform === 'bigcommerce'}
                />
                <Button
                  primary
                  onClick={handleAdd}
                  disabled={!newName.trim() || (platform !== 'shopify' && platform !== 'bigcommerce' && !newUrl.trim())}
                >
                  {addButtonText}
                </Button>
              </FormLayout>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card title="Your Sites" sectioned>
              {sites.length === 0 ? (
                <p>Connect your first site to get started.</p>
              ) : (
                sites.map((site) => <SiteCard key={site.id} site={site} onDelete={handleDeleteSite} />)
              )}
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  );
}